import type { AppType } from "@bandwith/services";
import type { ClientRequestOptions, ClientResponse } from "hono/client";
import { hc } from "hono/client";

/** Extract JSON response type from a Hono RPC endpoint */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type InferResponseType<T extends (...args: any[]) => any> = Awaited<
  ReturnType<Awaited<ReturnType<T>>["json"]>
>;

export type ApiClient = ReturnType<typeof hc<AppType>>;

/**
 * Response payload of a Hono RPC endpoint, read off the `ClientResponse` type
 * argument instead of `.json()`.
 *
 * `InferResponseType` goes through `ClientResponse["json"]`, which widens to
 * `unknown` when a route's payload union includes `undefined` — which happens
 * whenever a handler returns a destructured service result, since
 * `noUncheckedIndexedAccess` makes `const [x] = ...` optional. A `satisfies`
 * check against `unknown` accepts any shape, so the drift pin silently stops
 * pinning. This helper keeps the payload and drops the `undefined` arm.
 */
export type InferResponsePayload<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends (...args: any[]) => any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
> =
  Awaited<ReturnType<T>> extends ClientResponse<infer P, any, any>
    ? NonNullable<P>
    : never;

// Shared response types
export type SongListItem = InferResponseType<
  ApiClient["songs"]["$get"]
>[number];

// Song detail endpoint returns { error: string } | SongDetail, so extract the success case
type SongDetailResponse = InferResponseType<ApiClient["songs"][":id"]["$get"]>;
export type SongDetail = Extract<SongDetailResponse, { recordings: unknown }>;
export type SongRecording = SongDetail["recordings"][number];

export type MusicianItem = InferResponseType<
  ApiClient["musicians"]["$get"]
>[number];
export type FeedbackItem = InferResponseType<
  ApiClient["feedback"]["song"][":songId"]["$get"]
>[number];

// Mutation response types - used by MSW handlers to ensure type conformance
// Song creation returns `{ songId }` (not `{ id }`, unlike the update and
// delete responses below). Uses InferResponsePayload because this route's
// payload union includes `undefined`, which would otherwise widen to `unknown`.
export type SongCreateResponse = InferResponsePayload<
  ApiClient["songs"]["$post"]
>;
export type SongUpdateResponse = InferResponseType<
  ApiClient["songs"][":id"]["$patch"]
>;
export type SongDeleteResponse = InferResponseType<
  ApiClient["songs"][":id"]["$delete"]
>;
export type RecordingCreateResponse = InferResponseType<
  ApiClient["songs"][":id"]["recordings"]["$post"]
>;
export type FeedbackCreateResponse = InferResponseType<
  ApiClient["feedback"]["$post"]
>;
export type UploadUrlResponse = InferResponseType<
  ApiClient["upload"]["url"]["$get"]
>;
export type AuthMeResponse = InferResponseType<ApiClient["auth"]["me"]["$get"]>;

/** Factory for creating typed API clients */
export function createApi(baseUrl: string, options?: ClientRequestOptions) {
  return hc<AppType>(baseUrl, options);
}

export type { AppType };

// Mock API type conformance utilities
export type {
  ApiResponseMap,
  Equals,
  Expect,
  ExtractResponseMap,
} from "./msw-types";
